import { Data } from "@core/content/types";
import {
  ContentData,
  configureCreatorContentStore,
  getCreatorPayload,
  initializeCreatorData as initializeCreatorStore,
} from "./contentStore";
import {
  extractArtworkInputsFromPayload,
  generateImageTargets,
  type ImageTargetDescriptor,
} from "@webcore/core/targets/target.image.8w";
import Subtitles from "@assets/content/subtitles.srt?url";

// Geolocation config for Miami Beach mural (Gold Coast). Consumed by
// ImageRoute (geo-gate) and TwinRoute (launch button).
export const GEOGATE_CONFIG = {
  lat: "-28.06853072112715",
  lon: "153.444298146031",
  radiusKm: 5,
} as const;

// Live bindings populated by initializeCreatorData() — re-exported for consumers.
export {
  ContentData,
  MicrositeData,
  INITIAL_CONTENT_ID,
  getContent,
} from "./contentStore";

// Set this once per project:
// - Local file: new URL("./creatorData.json", import.meta.url).href
// - URL:        "https://launch.eyejack.io/Artwork-... /json"
export const CREATOR_DATA_SOURCE =
  "https://launch.eyejack.io/Artwork-684a1d85-2559-432f-920c-f44ec71b5762/json";

// Manual content for non-creator items (scenes, models, etc.).
// Appended after creator content — does NOT override creator entries by targetName.
export const MANUAL_CONTENT: Data[] = [];

configureCreatorContentStore({
  creatorDataSource: CREATOR_DATA_SOURCE,
  manualContent: MANUAL_CONTENT,
});

// Cache for generated image targets (populated lazily)
let generatedImageTargets: ImageTargetDescriptor[] | null = null;
let imageTargetsPromise: Promise<ImageTargetDescriptor[]> | null = null;

const getImageTargetSourcePayload = () => {
  if (typeof window !== "undefined" && (window as any).__ARTWORK_DATA__) {
    return (window as any).__ARTWORK_DATA__;
  }

  return getCreatorPayload();
};

/**
 * Project-specific overrides the creator payload doesn't carry.
 *
 * The creator payload supplies the video + target dimensions; the local
 * subtitle track is layered on here by matching the image content (the
 * `MANUAL_CONTENT` merge appends rather than overrides, so a targeted
 * post-init patch is the right tool).
 *
 * NOTE: `subtitles.srt` must match the creator artwork's video. If the
 * artwork behind CREATOR_DATA_SOURCE changes, update that asset (or remove
 * this override).
 */
const applyManualOverrides = (): void => {
  for (const item of ContentData) {
    if (item.type === "image" && item.content?.some((c) => c.type === "video")) {
      item.subtitle = { path: Subtitles, format: "srt" };
    }
  }
};

/**
 * Fetch creator artwork data, then layer on project-specific overrides.
 * Called once at boot (main.tsx) before the UI/engine read ContentData.
 */
export const initializeCreatorData = async (): Promise<void> => {
  await initializeCreatorStore();
  applyManualOverrides();
};

/**
 * Get image target data with client-side image generation (async version).
 * Generates target images in the browser using the Canvas API.
 */
export const getImageTargetDataAsync = async (): Promise<ImageTargetDescriptor[]> => {
  // Check if pre-built targets are injected (SSR / launch-v4)
  if ((window as any).__IMAGE_TARGET_DATA__?.length > 0) {
    return (window as any).__IMAGE_TARGET_DATA__;
  }

  // Check if already generated
  if (generatedImageTargets) {
    return generatedImageTargets;
  }

  // Return existing promise if generation is in progress
  if (imageTargetsPromise) {
    return imageTargetsPromise;
  }

  // Generate client-side using Canvas API
  const artworkPayload = getImageTargetSourcePayload();
  const artworkInputs = extractArtworkInputsFromPayload(artworkPayload);
  imageTargetsPromise = generateImageTargets(artworkInputs).then((targets) => {
    generatedImageTargets = targets;
    return targets;
  });

  return imageTargetsPromise;
};
